import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  applySkillExecution,
  cancelSkillExecution,
  getDocumentSkill,
  getDocumentTemplateDetail,
  listDocumentSkills,
  resolveSkillApplyTarget,
  startSkillExecution,
} from '@/api/documentWorkbenchApi';
import { formatApiErrorMessage } from '@/api/http';
import { buildSmartDocAtLabel } from '@/types/documentWorkbench';
import type {
  DocumentSkillDetail,
  DocumentSkillListItem,
  ResolveSkillApplyTargetResult,
  SkillExecutionCitation,
  SkillExecutionDetail,
  SmartDocAtKind,
  SmartDocAtToken,
} from '@/types/documentWorkbench';

/**
 * 智能文档工作台 ↔ 通用 AiAssistantPanel 共享状态：
 * Skill 选择、@ 提示词、执行结果写回、AI 会话引用。
 */
export const useSmartDocumentAiStore = defineStore('smartDocumentAi', () => {
  const active = ref(false);
  const workspaceId = ref('0');
  const personalWorkspace = ref(true);
  const documentId = ref<string | undefined>();
  const documentTitle = ref<string | undefined>();
  const skills = ref<DocumentSkillListItem[]>([]);
  const selectedSkillId = ref('');
  const selectedSkillDetail = ref<DocumentSkillDetail | null>(null);
  const atTokens = ref<SmartDocAtToken[]>([]);
  const execution = ref<SkillExecutionDetail | null>(null);
  const sending = ref(false);
  const lastAppliedDocumentId = ref<string | null>(null);
  /** 最近落库文档所属项目，用于跨工作台跳转打开 */
  const lastAppliedProjectId = ref<string | null>(null);
  const openSkillStudioRequest = ref(0);
  const skillStudioInitialMode = ref<'list' | 'create' | 'import'>('list');
  const openTemplateAuthorRequest = ref(0);
  const lastCitation = ref<SkillExecutionCitation | null>(null);
  /** 生成后待用户多轮补充的问题 */
  const pendingFollowUpQuestions = ref<string[]>([]);
  const pendingFollowUpHint = ref('');
  /** Skill 结果已生成，但落库目标（新建 vs 改当前）待用户澄清 */
  const pendingApplyTargetClarification = ref<{
    executionId: string;
    clarifyQuestion: string;
    currentDocumentId?: string;
    currentDocumentTitle?: string;
    originalUserInput?: string;
  } | null>(null);
  /** 智能文档页通用输出意图澄清（创建/合入/修正/问答） */
  const pendingOutputIntentClarification = ref<{
    originalText: string;
    previousClarifyQuestion: string;
    mentionedProjectId?: string;
    mentionedProjectName?: string;
  } | null>(null);
  /** 最近一次 Skill 绑定的项目 refId，供多轮补充时复用 */
  const lastSkillProjectRefId = ref('');
  const lastSkillProjectName = ref('');
  /** 模版库「使用」→ 请求 AI 面板插入 @模板 引用 */
  const attachTemplateRequest = ref(0);
  const pendingAttachTemplate = ref<{ id: string; name: string } | null>(null);
  /** AI 附件导入 / 外部请求 → 打开另存为模版向导 */
  const openSaveAsTemplateRequest = ref(0);
  const pendingSaveAsTemplate = ref<{ documentId: string; dialogTitle?: string } | null>(null);
  let sessionId = `smart-doc-${Date.now()}`;

  const skillDropdownOptions = computed(() => [
    { id: '', label: '选择 Skill' },
    ...skills.value.map((s) => ({
      id: s.id,
      label: s.name,
    })),
  ]);

  const selectedSkillName = computed(() => {
    const id = selectedSkillId.value;
    if (!id) return '';
    return (
      selectedSkillDetail.value?.name
      || skills.value.find((s) => s.id === id)?.name
      || ''
    );
  });

  const hasSkillAtToken = computed(() => atTokens.value.some((t) => t.kind === 'skill'));
  const hasTemplateAtToken = computed(() => atTokens.value.some((t) => t.kind === 'template'));
  const templateAtToken = computed(() => atTokens.value.find((t) => t.kind === 'template') || null);
  const skillAtTokens = computed(() => atTokens.value.filter((t) => t.kind === 'skill'));
  const selectedSkillIds = computed(() => skillAtTokens.value.map((t) => t.refId).filter(Boolean));

  function bindWorkspace(payload: {
    workspaceId: string;
    personalWorkspace: boolean;
  }) {
    active.value = true;
    workspaceId.value = payload.workspaceId;
    personalWorkspace.value = payload.personalWorkspace;
    sessionId = `smart-doc-${payload.workspaceId}-${Date.now()}`;
    void reloadSkills();
  }

  function unbind() {
    active.value = false;
    execution.value = null;
    atTokens.value = [];
    sending.value = false;
    selectedSkillDetail.value = null;
  }

  function setDocument(payload: { id?: string; title?: string } | null) {
    documentId.value = payload?.id;
    documentTitle.value = payload?.title;
  }

  function setSkillId(id: string) {
    selectedSkillId.value = id || '';
    if (!id) {
      selectedSkillDetail.value = null;
      return;
    }
    void loadSelectedSkillDetail(id);
  }

  async function loadSelectedSkillDetail(id: string) {
    try {
      selectedSkillDetail.value = await getDocumentSkill(id);
    } catch {
      const hit = skills.value.find((s) => s.id === id);
      selectedSkillDetail.value = hit
        ? {
            ...hit,
            currentVersion: hit.version,
          }
        : null;
    }
  }

  function requestOpenSkillStudio(mode: 'list' | 'create' | 'import' = 'list') {
    skillStudioInitialMode.value = mode;
    openSkillStudioRequest.value += 1;
  }

  /** 请求在 AI 面板中开启「对话创建模版」多轮流程（由 AiAssistantPanel 监听） */
  function requestBeginTemplateAuthoring() {
    openTemplateAuthorRequest.value += 1;
  }

  /** 模版库「使用」：仅引用进 AI，不立刻建文档 */
  function requestAttachTemplate(payload: { id: string; name: string }) {
    if (!payload?.id || !payload?.name) return;
    pendingAttachTemplate.value = { id: String(payload.id), name: String(payload.name) };
    attachTemplateRequest.value += 1;
  }

  /** 打开文档提炼模版向导（人工审核四阶段） */
  function requestOpenSaveAsTemplate(payload: { documentId: string; dialogTitle?: string }) {
    const documentId = String(payload?.documentId || '').trim();
    if (!documentId) return;
    pendingSaveAsTemplate.value = {
      documentId,
      dialogTitle: payload.dialogTitle || '创建模版',
    };
    openSaveAsTemplateRequest.value += 1;
  }

  async function reloadSkills() {
    try {
      skills.value = await listDocumentSkills(workspaceId.value);
      if (selectedSkillId.value && !skills.value.some((s) => s.id === selectedSkillId.value)) {
        selectedSkillId.value = '';
        selectedSkillDetail.value = null;
      } else if (selectedSkillId.value) {
        void loadSelectedSkillDetail(selectedSkillId.value);
      }
    } catch {
      skills.value = [];
    }
  }

  /**
   * 添加 / 替换行内引用上下文（输入框内以 @类型-名称 文本展示，无独立芯片）。
   * - 模版：全局仅允许 1 个（新引用替换旧模版）
   * - 技能：可多个（同 refId 去重）；最近选中的同步到 selectedSkillId
   */
  function addAtRef(payload: {
    kind: SmartDocAtKind;
    name: string;
    refId: string;
    /** 可选：自定义展示标签（含截断/「」包裹） */
    label?: string;
  }): { ok: boolean; message?: string; replacedTemplate?: boolean } {
    const kind = payload.kind;
    const name = (payload.name || '').trim();
    const refId = (payload.refId || '').trim();
    if (!name || !refId) {
      return { ok: false, message: '引用内容不完整' };
    }
    const label = (payload.label || '').trim() || buildSmartDocAtLabel(kind, name);
    if (kind === 'skill') {
      setSkillId(refId);
      const others = atTokens.value.filter((t) => !(t.kind === 'skill' && t.refId === refId));
      atTokens.value = [
        ...others,
        {
          id: `skill-${refId}`,
          kind: 'skill',
          name,
          label,
          refId,
        },
      ];
      return { ok: true };
    }
    if (kind === 'template') {
      const prev = atTokens.value.find((t) => t.kind === 'template');
      const replacedTemplate = Boolean(prev && prev.refId !== refId);
      atTokens.value = [
        ...atTokens.value.filter((t) => t.kind !== 'template'),
        {
          id: `template-${refId}`,
          kind: 'template',
          name,
          label,
          refId,
        },
      ];
      return { ok: true, replacedTemplate };
    }
    atTokens.value = [
      ...atTokens.value.filter((t) => !(t.kind === kind && t.refId === refId)),
      {
        id: `${kind}-${refId}-${Date.now()}`,
        kind,
        name,
        label,
        refId,
      },
    ];
    return { ok: true };
  }

  /** @deprecated 使用 addAtRef；保留兼容旧按钮入口 */
  function pickAt(kind: SmartDocAtKind): { ok: boolean; message?: string; openStudio?: boolean } {
    if (kind === 'skill') {
      if (!selectedSkillId.value) {
        return { ok: false, message: '请先通过 @技能 选择技能', openStudio: true };
      }
      return addAtRef({
        kind: 'skill',
        name: selectedSkillName.value || '技能',
        refId: selectedSkillId.value,
      });
    }
    if (kind === 'project') {
      return addAtRef({
        kind: 'project',
        name: documentTitle.value || '项目文档',
        refId: documentId.value || 'project-scope',
      });
    }
    if (kind === 'template') {
      return { ok: false, message: '请通过 @模版库 选择模版' };
    }
    return { ok: false, message: '未知引用类型' };
  }

  function removeAtToken(id: string) {
    const hit = atTokens.value.find((t) => t.id === id);
    atTokens.value = atTokens.value.filter((t) => t.id !== id);
    if (hit?.kind === 'skill' && hit.refId === selectedSkillId.value) {
      const remaining = atTokens.value.find((t) => t.kind === 'skill');
      setSkillId(remaining?.refId || '');
    }
  }

  function removeLastAtToken() {
    const list = atTokens.value;
    if (!list.length) return false;
    removeAtToken(list[list.length - 1].id);
    return true;
  }

  function clearAtTokens() {
    atTokens.value = [];
  }

  function buildChatContextPayload(): Record<string, unknown> | undefined {
    if (!active.value) return undefined;
    const detail = selectedSkillDetail.value;
    return {
      smartDocument: {
        workspaceId: workspaceId.value,
        personalWorkspace: personalWorkspace.value,
        documentId: documentId.value || null,
        documentTitle: documentTitle.value || null,
        skillId: selectedSkillId.value || null,
        skillIds: selectedSkillIds.value,
        skillName: selectedSkillName.value || null,
        skillCode: detail?.code || null,
        skillPromptSummary: detail?.promptText
          ? String(detail.promptText).slice(0, 1200)
          : null,
        skillSopSummary: detail?.sopText ? String(detail.sopText).slice(0, 600) : null,
        atTokens: atTokens.value.map((t) => ({
          kind: t.kind,
          name: t.name,
          label: t.label,
          refId: t.refId,
        })),
      },
    };
  }

  async function runSkillExecution(
    userInput: string,
    opts?: {
      signal?: AbortSignal;
      /** 发送前快照的 @ 引用；避免 UI clearAtTokens 后丢失项目/技能上下文 */
      tokens?: SmartDocAtToken[];
    },
  ): Promise<{
    ok: boolean;
    message?: string;
    execution?: SkillExecutionDetail;
  }> {
    if (!selectedSkillId.value) {
      return { ok: false, message: '请先通过 @技能 选择技能' };
    }
    const tokensForContext = (opts?.tokens && opts.tokens.length)
      ? opts.tokens
      : atTokens.value;
    const projectToken = tokensForContext.find((t) => t.kind === 'project');
    if (projectToken?.refId) {
      lastSkillProjectRefId.value = projectToken.refId;
      lastSkillProjectName.value = projectToken.name || '';
    }
    // 发送前校验 @模板 仍可用；失效则提示并保留其它输入
    const templateTokens = tokensForContext.filter((t) => t.kind === 'template');
    for (const token of templateTokens) {
      try {
        const detail = await getDocumentTemplateDetail(token.refId);
        if (detail.status !== 'published' && detail.status !== 'draft') {
          return {
            ok: false,
            message: `模版「${token.name}」未发布或已停用，请移除后再试`,
          };
        }
      } catch {
        return {
          ok: false,
          message: `模版「${token.name}」不可用或无权访问，请移除后再试`,
        };
      }
    }
    sending.value = true;
    try {
      const skillToken = tokensForContext.find((t) => t.kind === 'skill');
      const contextJson = JSON.stringify({
        tokens: tokensForContext.map((t) => ({
          kind: t.kind,
          name: t.name,
          label: t.label,
          refId: t.refId,
        })),
        documentId: documentId.value || null,
        documentTitle: documentTitle.value || null,
        skill: selectedSkillDetail.value
          ? {
              id: selectedSkillDetail.value.id,
              code: selectedSkillDetail.value.code,
              name: selectedSkillDetail.value.name,
            }
          : (skillToken
            ? { id: skillToken.refId, code: null, name: skillToken.name }
            : null),
      });
      const result = await startSkillExecution(
        workspaceId.value,
        {
          skillId: selectedSkillId.value,
          documentId: documentId.value,
          userInput,
          sessionId,
          contextJson,
        },
        opts,
      );
      execution.value = result;
      return { ok: true, execution: result };
    } catch (e) {
      if (opts?.signal?.aborted) {
        return { ok: false, message: '已终止' };
      }
      return { ok: false, message: formatApiErrorMessage(e) };
    } finally {
      sending.value = false;
    }
  }

  async function cancelExecution() {
    if (!execution.value) return;
    await cancelSkillExecution(execution.value.executionId);
    execution.value = { ...execution.value, status: 'cancelled' };
  }

  async function applyExecution(createNew: boolean): Promise<string | null> {
    if (!execution.value) return null;
    const followUps = [...(execution.value.followUpQuestions || [])];
    const result = await applySkillExecution(execution.value.executionId, {
      documentId: createNew ? undefined : documentId.value,
      createNewDocument: createNew,
      title: createNew
        ? (execution.value.resultTitle
          || `${selectedSkillDetail.value?.name || documentTitle.value || '文档'}-AI生成`)
        : undefined,
    });
    lastAppliedDocumentId.value = result.documentId;
    lastAppliedProjectId.value = workspaceId.value || null;
    if (result.documentId) {
      documentId.value = result.documentId;
      if (result.title) documentTitle.value = result.title;
    }
    pendingFollowUpQuestions.value = followUps;
    pendingFollowUpHint.value = followUps.length
      ? '请直接回复上述待确认项，我将据此更新文档。'
      : '';
    pendingApplyTargetClarification.value = null;
    execution.value = null;
    return result.documentId;
  }

  /**
   * 由 LLM 判定写入目标；意图不清时写入 pendingApplyTargetClarification，不落库。
   * @returns applied | clarify | failed
   */
  async function resolveAndApplyExecution(userInput: string): Promise<{
    status: 'applied' | 'clarify' | 'failed';
    createNew?: boolean;
    documentId?: string | null;
    clarifyQuestion?: string;
    message?: string;
  }> {
    if (!execution.value?.executionId) {
      return { status: 'failed', message: '没有可写入的 Skill 执行结果' };
    }
    const executionId = execution.value.executionId;
    const pending = pendingApplyTargetClarification.value;
    try {
      const decision: ResolveSkillApplyTargetResult = await resolveSkillApplyTarget(executionId, {
        userInput,
        currentDocumentId: documentId.value || pending?.currentDocumentId,
        currentDocumentTitle: documentTitle.value || pending?.currentDocumentTitle,
        previousClarifyQuestion: pending?.clarifyQuestion,
      });
      if (decision.decision === 'create_new' || decision.decision === 'update_current') {
        const createNew = decision.decision === 'create_new';
        const docId = await applyExecution(createNew);
        if (!docId) {
          return { status: 'failed', message: '写入文档失败' };
        }
        return { status: 'applied', createNew, documentId: docId };
      }
      const question = (decision.clarifyQuestion || '').trim()
        || `请问是要新建一份文档，还是基于当前打开的「${documentTitle.value || '文档'}」进行修改？请直接回复「新建文档」或「修改当前文档」。`;
      pendingApplyTargetClarification.value = {
        executionId,
        clarifyQuestion: question,
        currentDocumentId: documentId.value,
        currentDocumentTitle: documentTitle.value,
        originalUserInput: pending?.originalUserInput || userInput,
      };
      // 保留 execution 供澄清后写入
      return { status: 'clarify', clarifyQuestion: question };
    } catch (e) {
      return { status: 'failed', message: formatApiErrorMessage(e) };
    }
  }

  function clearPendingFollowUp() {
    pendingFollowUpQuestions.value = [];
    pendingFollowUpHint.value = '';
  }

  function clearPendingApplyTargetClarification() {
    pendingApplyTargetClarification.value = null;
  }

  function clearPendingOutputIntentClarification() {
    pendingOutputIntentClarification.value = null;
  }

  function clearExecution() {
    execution.value = null;
    pendingApplyTargetClarification.value = null;
  }

  function markDocumentApplied(docId: string, projectId?: string | null) {
    const id = String(docId || '').trim();
    if (!id) return;
    // 先清空再赋值，确保同 id 重复落库也能触发工作台 watch
    lastAppliedDocumentId.value = null;
    lastAppliedProjectId.value = projectId != null && String(projectId).trim()
      ? String(projectId).trim()
      : (workspaceId.value || null);
    lastAppliedDocumentId.value = id;
    documentId.value = id;
  }

  function emitCitation(citation: SkillExecutionCitation) {
    lastCitation.value = citation;
  }

  return {
    active,
    workspaceId,
    personalWorkspace,
    documentId,
    documentTitle,
    skills,
    selectedSkillId,
    selectedSkillDetail,
    atTokens,
    execution,
    sending,
    lastAppliedDocumentId,
    lastAppliedProjectId,
    openSkillStudioRequest,
    skillStudioInitialMode,
    openTemplateAuthorRequest,
    attachTemplateRequest,
    pendingAttachTemplate,
    openSaveAsTemplateRequest,
    pendingSaveAsTemplate,
    lastCitation,
    pendingFollowUpQuestions,
    pendingFollowUpHint,
    pendingApplyTargetClarification,
    pendingOutputIntentClarification,
    lastSkillProjectRefId,
    lastSkillProjectName,
    skillDropdownOptions,
    selectedSkillName,
    hasSkillAtToken,
    hasTemplateAtToken,
    templateAtToken,
    skillAtTokens,
    selectedSkillIds,
    bindWorkspace,
    unbind,
    setDocument,
    setSkillId,
    requestOpenSkillStudio,
    requestBeginTemplateAuthoring,
    requestAttachTemplate,
    requestOpenSaveAsTemplate,
    reloadSkills,
    addAtRef,
    pickAt,
    removeAtToken,
    removeLastAtToken,
    clearAtTokens,
    buildChatContextPayload,
    runSkillExecution,
    cancelExecution,
    applyExecution,
    resolveAndApplyExecution,
    clearPendingFollowUp,
    clearPendingApplyTargetClarification,
    clearPendingOutputIntentClarification,
    clearExecution,
    markDocumentApplied,
    emitCitation,
  };
});
